/* =============================================================
   StaffCRMPage — /staff/crm
   Professional mobile-first CRM with smart lead scoring
   - Smart urgency score: callTime + travelTimeline + serviceName
   - "Яаралтай залгах" section for hot leads (score ≥70)
   - Card-based layout (mobile-friendly)
   - Quick notes, direct call/email buttons
   - Call-waiting alert badge for callTime="now" + status="new"
   ============================================================= */
import { useState, useMemo } from "react";
import {
  Search, Filter, Phone, Mail, Globe, Calendar,
  Clock, User, X, Check, ArrowLeft,
  TrendingUp, AlertCircle, RefreshCw, CheckCircle2,
  Trash2, PhoneCall, Zap, Flame, ChevronDown, ChevronUp,
  MessageCircle, Star, BarChart3, UserPlus, Users, Plus, Send, Building2,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Link } from "wouter";
import { isUnclaimedLead, STAFF_MEMBERS } from "@shared/staff";
import { toast } from "sonner";

/* ── Types ───────────────────────────────────────────────────── */
type Status = "new" | "phone_consulted" | "callback" | "office_consulted";
type FilterStatus = "all" | Status;

/* ── Constants ───────────────────────────────────────────────── */
const STATUS_CONFIG: Record<Status, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  new:              { label: "Шинэ",                  color: "#60a5fa", bg: "rgba(96,165,250,0.15)",  icon: <AlertCircle className="w-3.5 h-3.5" /> },
  phone_consulted:  { label: "Утсаар зөвлөгөө өгсөн", color: "#34d399", bg: "rgba(52,211,153,0.15)",  icon: <PhoneCall className="w-3.5 h-3.5" /> },
  callback:         { label: "Дахин залгах",          color: "#fbbf24", bg: "rgba(251,191,36,0.15)",  icon: <RefreshCw className="w-3.5 h-3.5" /> },
  office_consulted: { label: "Оффист зөвлөгөө өгсөн", color: "#c084fc", bg: "rgba(192,132,252,0.15)", icon: <Building2 className="w-3.5 h-3.5" /> },
};

const COUNTRY_LABELS: Record<string, string> = {
  usa: "🇺🇸 АНУ", australia: "🇦🇺 Австрали", canada: "🇨🇦 Канад",
  ireland: "🇮🇪 Ирланд", uk: "🇬🇧 Их Британи", schengen: "🇪🇺 Шенген",
  czech: "🇨🇿 Чех", newzealand: "🇳🇿 Шинэ Зеланд", other: "🌍 Бусад",
};

const VISA_CATEGORIES_BY_COUNTRY: Record<string, string[]> = {
  usa: ["Аяллын виз (B1/B2)", "Сургалтын виз (F-1)", "АНУ-ын ахлах сургуулийн сурагч солилцоо", "Work and Travel", "2+2 — 16 наснаас ахлах сургуульд үргэлжлүүлэн дээд сургуульд суралцах хөтөлбөр", "Ажлын виз / Ногоон карт (EB3/NIW/H1B)", "Цагаачлал"],
  australia: ["Аяллын виз", "Сургалтын виз", "Цагаачлал / Ажлын виз"],
  canada: ["Аяллын виз", "Сургалтын виз", "Цагаачлал (Express Entry)"],
  ireland: ["Аяллын виз", "Сургалт + Ажил"],
  uk: ["Аяллын виз", "Сургалтын виз"],
  schengen: ["Аяллын виз"],
  czech: ["Хөдөлмөрийн гэрээ"],
  newzealand: ["Мэргэжлээрээ ажиллах", "Аяллын виз", "Сургалтын виз"],
  other: ["Аяллын виз", "Сургалтын виз", "Ажлын виз", "Бусад"],
};

const CALL_TIME_LABELS: Record<string, string> = {
  now: "⚡ Яг одоо залгаарай", morning: "🌅 Өглөө", afternoon: "☀️ Үдээс хойш",
};

const CONTACT_APP_LABELS: Record<string, string> = {
  whatsapp: "WhatsApp",
  viber: "Viber",
  telegram: "Telegram",
};

/* ── Smart Lead Score ────────────────────────────────────────── */
function calcLeadScore(row: {
  callTime?: string | null;
  travelTimeline?: string | null;
  serviceName?: string | null;
  status?: string | null;
}): number {
  let score = 0;
  // Call time intent
  if (row.callTime === "now") score += 40;
  else if (row.callTime === "morning") score += 20;
  else if (row.callTime === "afternoon") score += 10;
  // Travel urgency
  const tl = (row.travelTimeline ?? "").toLowerCase();
  if (tl.includes("яг одоо") || tl.includes("энэ долоо") || tl.includes("энэ сар") || tl.includes("1 сар")) score += 40;
  else if (tl.includes("3 сар") || tl.includes("гурван")) score += 25;
  else if (tl.includes("6 сар") || tl.includes("зургаан")) score += 10;
  // Has selected a service (real intent)
  if (row.serviceName) score += 20;
  return Math.min(score, 100);
}

type UrgencyLevel = "hot" | "warm" | "cold";
function getUrgency(score: number): UrgencyLevel {
  if (score >= 70) return "hot";
  if (score >= 40) return "warm";
  return "cold";
}

const URGENCY_CONFIG: Record<UrgencyLevel, { label: string; color: string; bg: string; border: string; icon: React.ReactNode }> = {
  hot:  { label: "Яаралтай",  color: "#f87171", bg: "rgba(248,113,113,0.12)", border: "rgba(248,113,113,0.35)", icon: <Flame className="w-3.5 h-3.5" /> },
  warm: { label: "Дунд",      color: "#fbbf24", bg: "rgba(251,191,36,0.10)",  border: "rgba(251,191,36,0.30)",  icon: <Zap className="w-3.5 h-3.5" /> },
  cold: { label: "Энгийн",    color: "#6ee7b7", bg: "rgba(110,231,183,0.08)", border: "rgba(110,231,183,0.20)", icon: <Star className="w-3.5 h-3.5" /> },
};

/* ── Helpers ─────────────────────────────────────────────────── */
function formatDate(d: Date | string | null | undefined) {
  if (!d) return "—";
  return new Date(d).toLocaleString("mn-MN", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function timeAgo(d: Date | string | null | undefined) {
  if (!d) return "";
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "дөнгөж сая";
  if (mins < 60) return `${mins} мин өмнө`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} цаг өмнө`;
  const days = Math.floor(hrs / 24);
  return `${days} өдөр өмнө`;
}

function toDateTimeInputValue(d: Date | string | null | undefined) {
  if (!d) return "";
  const date = new Date(d);
  const timezoneOffset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - timezoneOffset).toISOString().slice(0, 16);
}

function probabilityColor(value: number | null | undefined) {
  const probability = value ?? 0;
  return probability >= 70 ? "#34d399" : probability >= 40 ? "#fbbf24" : "#f87171";
}

/* ── Lead Card ───────────────────────────────────────────────── */
function LeadCard({
  row,
  selected,
  onClick,
  onQuickNote,
  onClaim,
}: {
  row: any;
  selected: boolean;
  onClick: () => void;
  onQuickNote: (id: number, note: string) => void;
  onClaim: (id: number) => void;
}) {
  const score = calcLeadScore(row);
  const urgency = getUrgency(score);
  const uc = URGENCY_CONFIG[urgency];
  const sc = STATUS_CONFIG[row.status as Status] ?? STATUS_CONFIG.new;
  const isWaiting = row.callTime === "now" && row.status === "new";
  const isUnclaimed = isUnclaimedLead(row.assignedTo);
  const [showNote, setShowNote] = useState(false);
  const [noteVal, setNoteVal] = useState(row.notes ?? "");

  return (
    <div
      className="rounded-2xl transition-all cursor-pointer"
      style={{
        background: selected
          ? "rgba(124,58,237,0.18)"
          : urgency === "hot"
          ? "rgba(248,113,113,0.06)"
          : "rgba(255,255,255,0.04)",
        border: selected
          ? "1px solid rgba(124,58,237,0.50)"
          : urgency === "hot"
          ? `1px solid ${uc.border}`
          : "1px solid rgba(255,255,255,0.08)",
      }}
    >
      {/* Call-waiting alert banner */}
      {isWaiting && (
        <div
          className="flex items-center gap-2 px-4 py-2 rounded-t-2xl text-xs font-bold"
          style={{ background: "rgba(248,113,113,0.20)", color: "#fca5a5", borderBottom: "1px solid rgba(248,113,113,0.25)" }}
        >
          <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
          Залгахыг хүлээж байна — яг одоо залгаарай!
        </div>
      )}

      <div className="p-4" onClick={onClick}>
        <div className="flex items-start justify-between gap-3">
          {/* Left: Name + contact */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-white font-bold text-base leading-tight">{row.name}</span>
              {/* Urgency badge */}
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold"
                style={{ background: uc.bg, color: uc.color, border: `1px solid ${uc.border}` }}
              >
                {uc.icon}
                {uc.label}
              </span>
              {/* Status badge */}
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
                style={{ background: sc.bg, color: sc.color }}
              >
                {sc.icon}
                {sc.label}
              </span>
            </div>

            {/* Contact row */}
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <a
                href={`tel:${row.phone}`}
                onClick={e => e.stopPropagation()}
                className="flex items-center gap-1.5 text-sm font-semibold transition-colors"
                style={{ color: urgency === "hot" ? "#fca5a5" : "#93c5fd" }}
              >
                <Phone className="w-3.5 h-3.5" />
                {row.phone}
              </a>
              {row.email && (
                <a
                  href={`mailto:${row.email}`}
                  onClick={e => e.stopPropagation()}
                  className="flex items-center gap-1 text-xs text-white/40 hover:text-white/70 transition-colors truncate max-w-[180px]"
                >
                  <Mail className="w-3 h-3 flex-shrink-0" />
                  {row.email}
                </a>
              )}
              {row.contactApp && row.contactAppNumber && (
                <span className="flex items-center gap-1 text-xs font-semibold text-sky-200/85">
                  💬 {CONTACT_APP_LABELS[row.contactApp] ?? row.contactApp}: {row.contactAppNumber}
                </span>
              )}
            </div>

            {/* Country + visa + service */}
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
              <span className="text-white/60 text-xs">{COUNTRY_LABELS[row.country] ?? row.country}</span>
              {row.visaCategory && (
                <span className="text-white/40 text-xs truncate max-w-[160px]">{row.visaCategory}</span>
              )}
              {row.consultationLocation && (
                <span className="text-sky-200/75 text-xs">📍 {row.consultationLocation}-аас</span>
              )}
              {row.serviceName && (
                <span className="text-emerald-400 text-xs font-semibold">
                  {row.servicePrice ? `₮${row.servicePrice.toLocaleString()}` : row.serviceName}
                </span>
              )}
            </div>

            {/* Timeline + callTime */}
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5">
              {row.travelTimeline && (
                <span className="flex items-center gap-1 text-xs" style={{ color: urgency === "hot" ? "#fbbf24" : "rgba(255,255,255,0.45)" }}>
                  <Calendar className="w-3 h-3" />
                  {row.travelTimeline}
                </span>
              )}
              <span className="flex items-center gap-1 text-xs" style={{ color: row.callTime === "now" ? "#f87171" : "rgba(255,255,255,0.35)" }}>
                <Clock className="w-3 h-3" />
                {CALL_TIME_LABELS[row.callTime] ?? row.callTime}
              </span>
              <span className="text-white/25 text-xs">{timeAgo(row.createdAt)}</span>
            </div>

            {row.requestNote && (
              <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-white/48">💬 {row.requestNote}</p>
            )}

            {row.probability !== null && (
              <div className="mt-2 flex items-center gap-2 text-xs">
                <TrendingUp className="h-3.5 w-3.5" style={{ color: probabilityColor(row.probability) }} />
                <span className="text-white/45">Гэрээ хийх магадлал</span>
                <div className="h-1.5 w-20 overflow-hidden rounded-full bg-white/10">
                  <div className="h-full rounded-full" style={{ width: `${row.probability ?? 0}%`, background: probabilityColor(row.probability) }} />
                </div>
                <span className="font-black" style={{ color: probabilityColor(row.probability) }}>{row.probability ?? 0}%</span>
              </div>
            )}

            {row.officeAppointmentAt && (
              <p className="mt-2 flex items-center gap-1.5 text-xs text-violet-300"><Building2 className="h-3.5 w-3.5" />Оффист: {formatDate(row.officeAppointmentAt)}</p>
            )}

            {/* Notes preview */}
            {row.notes && !showNote && (
              <p className="mt-2 text-white/40 text-xs italic truncate max-w-xs">📝 {row.notes}</p>
            )}
          </div>

          {/* Right: Score circle */}
          <div className="flex flex-col items-center gap-1 flex-shrink-0">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center font-black text-lg"
              style={{
                background: urgency === "hot" ? "rgba(248,113,113,0.20)" : urgency === "warm" ? "rgba(251,191,36,0.15)" : "rgba(110,231,183,0.10)",
                border: `2px solid ${uc.color}40`,
                color: uc.color,
              }}
            >
              {score}
            </div>
            <span className="text-white/30 text-[10px]">оноо</span>
            {row.assignedTo && <span className="text-white/35 text-[10px] text-center leading-tight">{row.assignedTo}</span>}
          </div>
        </div>

        {/* Quick note toggle */}
        <div className="mt-3 flex items-center gap-2" onClick={e => e.stopPropagation()}>
          <button
            onClick={() => setShowNote(!showNote)}
            className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg transition-all"
            style={{ background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.50)", border: "1px solid rgba(255,255,255,0.10)" }}
          >
            <MessageCircle className="w-3 h-3" />
            {showNote ? "Хаах" : row.notes ? "Тэмдэглэл засах" : "Тэмдэглэл нэмэх"}
            {showNote ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {isUnclaimed ? (
            <button
              type="button"
              onClick={() => onClaim(row.id)}
              className="ml-auto inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-black transition-all hover:opacity-85"
              style={{ background: "rgba(96,165,250,0.16)", color: "#93c5fd", border: "1px solid rgba(96,165,250,0.35)" }}
            >
              <UserPlus className="h-3.5 w-3.5" />
              Өөрийн болгох
            </button>
          ) : (
            <span className="ml-auto text-white/45 text-xs">👤 Хариуцагч: <strong className="text-white/80">{row.assignedTo}</strong></span>
          )}
        </div>

        {/* Inline note editor */}
        {showNote && (
          <div className="mt-2" onClick={e => e.stopPropagation()}>
            <textarea
              rows={3}
              placeholder="Тэмдэглэл бичих... (автоматаар хадгалагдана)"
              value={noteVal}
              onChange={e => setNoteVal(e.target.value)}
              onBlur={() => onQuickNote(row.id, noteVal)}
              autoFocus
              className="w-full px-3 py-2 rounded-xl text-white text-sm outline-none placeholder-white/25 resize-none"
              style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)" }}
            />
            <p className="text-white/25 text-[11px] mt-1">Blur хийхэд автоматаар хадгалагдана</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Detail Panel ────────────────────────────────────────────── */
function DetailPanel({
  id,
  onClose,
  onUpdated,
  staffActionsEnabled,
}: {
  id: number;
  onClose: () => void;
  onUpdated: () => void;
  staffActionsEnabled: boolean;
}) {
  const { data: item, isLoading, refetch: refetchItem } = trpc.consultation.getById.useQuery({ id });
  const updateMut = trpc.consultation.update.useMutation({
    onSuccess: async () => { await refetchItem(); onUpdated(); },
    onError: () => toast.error("Өөрчлөлт хадгалагдсангүй. Дахин оролдоно уу."),
  });
  const deleteMut = trpc.consultation.delete.useMutation({ onSuccess: () => { onUpdated(); onClose(); } });
  const sendVisaInfoMut = trpc.consultation.sendVisaInfo.useMutation({
    onSuccess: (result) => toast.success(`${result.email} хаяг руу визийн танилцуулга илгээгдлээ.`),
    onError: (error) => toast.error(error.message || "Имэйл илгээхэд алдаа гарлаа."),
  });

  const [editNotes, setEditNotes] = useState<string | null>(null);
  const [editProb, setEditProb] = useState<string | null>(null);
  const [emailCountry, setEmailCountry] = useState<string | null>(null);
  const [emailVisaCategory, setEmailVisaCategory] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (isLoading || !item) {
    return (
      <div className="flex items-center justify-center h-full min-h-[200px]">
        <div className="w-6 h-6 rounded-full border-2 border-violet-400/30 border-t-violet-400 animate-spin" />
      </div>
    );
  }

  const score = calcLeadScore(item);
  const urgency = getUrgency(score);
  const uc = URGENCY_CONFIG[urgency];
  const sc = STATUS_CONFIG[item.status as Status];
  const isWaiting = item.callTime === "now" && item.status === "new";

  const saveField = (field: string, value: unknown) => {
    updateMut.mutate({ id, [field]: value });
  };

  const saveProbability = () => {
    const probability = Number(editProb ?? item.probability ?? 0);
    updateMut.mutate(
      { id, probability },
      {
        onSuccess: () => {
          setEditProb(null);
          toast.success(`Гэрээ хийх магадлалыг ${probability}% болгон хадгаллаа.`);
        },
      },
    );
  };

  const visaEmailCountry = emailCountry ?? item.country;
  const visaEmailCategory = emailVisaCategory ?? item.visaCategory;
  const visaOptions = VISA_CATEGORIES_BY_COUNTRY[visaEmailCountry] ?? [];

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Header */}
      <div
        className="flex items-start justify-between p-5"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-white font-bold text-lg leading-tight">{item.name}</h2>
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold"
              style={{ background: uc.bg, color: uc.color, border: `1px solid ${uc.border}` }}
            >
              {uc.icon} {uc.label} ({score})
            </span>
          </div>
          <p className="text-white/35 text-xs mt-1">#{item.id} · {formatDate(item.createdAt)}</p>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors text-white/50 ml-2 flex-shrink-0">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex flex-col gap-4 p-5">
        {/* Call-waiting alert */}
        {isWaiting && (
          <div
            className="flex items-center gap-3 p-4 rounded-xl"
            style={{ background: "rgba(248,113,113,0.15)", border: "1px solid rgba(248,113,113,0.40)" }}
          >
            <span className="w-3 h-3 rounded-full bg-red-400 animate-pulse flex-shrink-0" />
            <div>
              <p className="text-red-300 font-bold text-sm">Залгахыг хүлээж байна!</p>
              <p className="text-red-400/70 text-xs mt-0.5">Энэ хэрэглэгч яг одоо залгахыг хүссэн байна</p>
            </div>
          </div>
        )}

        {/* Quick call buttons */}
        <div className={`grid gap-2 ${item.email ? "grid-cols-2" : "grid-cols-1"}`}>
          <a
            href={`tel:${item.phone}`}
            className="flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm transition-all hover:opacity-80"
            style={{ background: "rgba(52,211,153,0.15)", color: "#34d399", border: "1px solid rgba(52,211,153,0.35)" }}
          >
            <Phone className="w-4 h-4" />
            Залгах
          </a>
          {item.email && (
            <a
              href={`mailto:${item.email}`}
              className="flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm transition-all hover:opacity-80"
              style={{ background: "rgba(96,165,250,0.12)", color: "#60a5fa", border: "1px solid rgba(96,165,250,0.30)" }}
            >
              <Mail className="w-4 h-4" />
              Имэйл
            </a>
          )}
        </div>

        {(item.contactApp && item.contactAppNumber) || item.requestNote ? (
          <div
            className="rounded-xl p-4"
            style={{ background: "rgba(56,189,248,0.10)", border: "1px solid rgba(56,189,248,0.38)" }}
          >
            <p className="mb-3 text-xs font-black uppercase tracking-wide text-sky-100">Илгээсэн хүсэлт ба холбоо барих апп</p>
            {item.contactApp && item.contactAppNumber && (
              <div className="flex items-center gap-2 rounded-lg bg-sky-300/[0.09] px-3 py-2.5 text-sm font-bold text-sky-100">
                <MessageCircle className="h-4 w-4 flex-shrink-0 text-sky-300" />
                <span>{CONTACT_APP_LABELS[item.contactApp] ?? item.contactApp}: {item.contactAppNumber}</span>
              </div>
            )}
            {item.requestNote && (
              <div className={item.contactApp && item.contactAppNumber ? "mt-3" : ""}>
                <p className="mb-1 text-xs font-semibold text-sky-100/65">Үйлчлүүлэгчийн илгээсэн хүсэлт</p>
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-white/90">{item.requestNote}</p>
              </div>
            )}
          </div>
        ) : null}

        {/* Contact info */}
        <div
          className="rounded-xl p-4 flex flex-col gap-2.5"
          style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
        >
          <p className="text-white/35 text-xs font-semibold uppercase tracking-wide mb-1">Мэдээлэл</p>
          <div className="flex items-center gap-2 text-sm text-white/70">
            <Globe className="w-3.5 h-3.5 text-white/35 flex-shrink-0" />
            <span>{COUNTRY_LABELS[item.country] ?? item.country}</span>
            {item.visaCategory && <span className="text-white/40 truncate">— {item.visaCategory}</span>}
          </div>
          {item.consultationLocation && (
            <div className="flex items-center gap-2 text-sm text-sky-200/80">
              <Globe className="w-3.5 h-3.5 text-sky-300/60 flex-shrink-0" />
              <span>Зөвлөгөө авч буй газар: {item.consultationLocation}</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-sm" style={{ color: urgency === "hot" ? "#fbbf24" : "rgba(255,255,255,0.70)" }}>
            <Calendar className="w-3.5 h-3.5 flex-shrink-0" style={{ color: urgency === "hot" ? "#fbbf24" : "rgba(255,255,255,0.35)" }} />
            <span>{item.travelTimeline}</span>
          </div>
          <div className="flex items-center gap-2 text-sm" style={{ color: item.callTime === "now" ? "#f87171" : "rgba(255,255,255,0.70)" }}>
            <Clock className="w-3.5 h-3.5 flex-shrink-0" style={{ color: item.callTime === "now" ? "#f87171" : "rgba(255,255,255,0.35)" }} />
            <span>{CALL_TIME_LABELS[item.callTime] ?? item.callTime}</span>
          </div>
          {item.serviceName && (
            <div className="flex items-start gap-2 text-sm">
              <TrendingUp className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white/70">{item.serviceName}</p>
                {item.servicePrice ? (
                  <p className="text-emerald-400 font-semibold text-xs mt-0.5">₮{item.servicePrice.toLocaleString()}</p>
                ) : null}
              </div>
            </div>
          )}
        </div>

        {/* Status */}
        <div>
          <p className="text-white/35 text-xs font-semibold uppercase tracking-wide mb-2">Статус</p>
          <div className="grid grid-cols-2 gap-2">
            {(Object.keys(STATUS_CONFIG) as Status[]).map(s => {
              const cfg = STATUS_CONFIG[s];
              const active = item.status === s;
              return (
                <button
                  key={s}
                  onClick={() => saveField("status", s)}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-bold transition-all"
                  style={{
                    background: active ? cfg.color : "rgba(255,255,255,0.04)",
                    border: `1px solid ${active ? cfg.color : "rgba(255,255,255,0.08)"}`,
                    boxShadow: active ? `0 0 0 2px ${cfg.color}30` : "none",
                    color: active ? "#0D0A2E" : "rgba(255,255,255,0.58)",
                  }}
                >
                  {cfg.icon}
                  {cfg.label}
                  {active && <Check className="w-3 h-3 ml-auto" />}
                </button>
              );
            })}
          </div>
        </div>

        {item.lastContactedAt && (
          <p className="text-white/25 text-xs text-center -mt-2">Сүүлд холбогдсон: {formatDate(item.lastContactedAt)}</p>
        )}

        {/* Probability */}
        <div>
          <p className="text-white/35 text-xs font-semibold uppercase tracking-wide mb-2">
            <TrendingUp className="w-3 h-3 inline mr-1" />Гэрээ хийх магадлал
          </p>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.08)" }}>
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${editProb ?? (item.probability ?? 0)}%`,
                  background: probabilityColor(Number(editProb ?? item.probability ?? 0)),
                }}
              />
            </div>
            <span className="text-white font-bold text-sm w-10 text-right">
              {editProb ?? (item.probability ?? 0)}%
            </span>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <input
              type="range" min={0} max={100}
              value={editProb ?? (item.probability ?? 0)}
              onChange={e => setEditProb(e.target.value)}
              className="flex-1 h-2 rounded-full outline-none cursor-pointer accent-violet-500"
            />
            <button
              type="button"
              disabled={updateMut.isPending || editProb === null}
              onClick={saveProbability}
              className="inline-flex min-w-[104px] items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-black transition-all disabled:cursor-not-allowed disabled:opacity-45"
              style={{ background: "rgba(124,58,237,0.28)", color: "#d8b4fe", border: "1px solid rgba(192,132,252,0.45)" }}
            >
              <Check className="w-3.5 h-3.5" />
              {updateMut.isPending ? "Хадгалж..." : `${editProb ?? item.probability ?? 0}% хадгалах`}
            </button>
          </div>
          <p className="mt-1.5 text-xs text-white/35">Шинэ хувь сонгосны дараа <span className="font-semibold text-violet-200">“хадгалах”</span> дээр дарна. Амжилттай бол ногоон мэдэгдэл гарна.</p>
        </div>

        {/* Assigned to */}
        <div>
          <p className="text-white/35 text-xs font-semibold uppercase tracking-wide mb-2">
            <User className="w-3 h-3 inline mr-1" />Хариуцах ажилтан
          </p>
          <div className="rounded-lg px-3 py-2.5 text-sm" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }}>
            {item.assignedTo ? <span className="font-bold text-white">👤 {item.assignedTo}</span> : <span className="text-white/45">Одоогоор хариуцах ажилтангүй</span>}
            <p className="mt-1 text-xs text-white/30">Авсан хүсэлтийг зөвхөн админ шилжүүлэх эсвэл суллах боломжтой.</p>
          </div>
        </div>

        {/* Callback at */}
        <div>
          <p className="text-white/35 text-xs font-semibold uppercase tracking-wide mb-2">
            <RefreshCw className="w-3 h-3 inline mr-1" />Дахин залгах цаг
          </p>
          <input
            type="datetime-local"
            value={toDateTimeInputValue(item.callbackAt)}
            onChange={e => saveField("callbackAt", e.target.value ? new Date(e.target.value).toISOString() : null)}
            className="w-full px-3 py-2.5 rounded-lg text-white text-sm outline-none"
            style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }}
          />
        </div>

        {/* Office appointment */}
        <div>
          <p className="text-white/35 text-xs font-semibold uppercase tracking-wide mb-2">
            <Building2 className="w-3 h-3 inline mr-1" />Оффист уулзах цаг
          </p>
          <input
            type="datetime-local"
            value={toDateTimeInputValue(item.officeAppointmentAt)}
            onChange={e => saveField("officeAppointmentAt", e.target.value ? new Date(e.target.value).toISOString() : null)}
            className="w-full px-3 py-2.5 rounded-lg text-white text-sm outline-none"
            style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }}
          />
          {item.officeAppointmentAt && <p className="mt-1.5 text-xs text-violet-300">Товлосон: {formatDate(item.officeAppointmentAt)}</p>}
        </div>

        {staffActionsEnabled && (
          <div className="rounded-xl border border-sky-300/20 bg-sky-300/[0.05] p-3.5">
            <p className="text-xs font-black uppercase tracking-wide text-sky-200"><Mail className="mr-1 inline h-3.5 w-3.5" />Визийн танилцуулга имэйлээр илгээх</p>
            <p className="mt-1 text-xs leading-5 text-white/45">Улс, визээ сонгоод {item.email} хаяг руу илгээнэ.</p>
            <div className="mt-3 grid gap-2">
              <select
                value={visaEmailCountry}
                onChange={e => {
                  const nextCountry = e.target.value;
                  setEmailCountry(nextCountry);
                  setEmailVisaCategory(VISA_CATEGORIES_BY_COUNTRY[nextCountry]?.[0] ?? "");
                }}
                className="w-full rounded-lg border border-white/10 bg-white/[0.06] px-3 py-2 text-sm text-white outline-none"
              >
                {Object.entries(COUNTRY_LABELS).map(([value, label]) => <option key={value} value={value} style={{ background: "#130D3A" }}>{label}</option>)}
              </select>
              <select
                value={visaEmailCategory}
                onChange={e => setEmailVisaCategory(e.target.value)}
                className="w-full rounded-lg border border-white/10 bg-white/[0.06] px-3 py-2 text-sm text-white outline-none"
              >
                {visaOptions.map(option => <option key={option} value={option} style={{ background: "#130D3A" }}>{option}</option>)}
              </select>
              <button
                type="button"
                disabled={sendVisaInfoMut.isPending || !visaEmailCategory}
                onClick={() => sendVisaInfoMut.mutate({ id, country: visaEmailCountry, visaCategory: visaEmailCategory })}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-sky-500 px-3 py-2.5 text-sm font-black text-white transition hover:bg-sky-400 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Send className="h-3.5 w-3.5" />{sendVisaInfoMut.isPending ? "Илгээж байна..." : "Танилцуулга илгээх"}
              </button>
            </div>
          </div>
        )}

        {/* Notes */}
        <div>
          <p className="text-white/35 text-xs font-semibold uppercase tracking-wide mb-2">
            <MessageCircle className="w-3 h-3 inline mr-1" />Тэмдэглэл
          </p>
          <textarea
            rows={4}
            placeholder="Ажилтны тэмдэглэл — яриа, санал, дараагийн алхам..."
            value={editNotes ?? (item.notes ?? "")}
            onChange={e => setEditNotes(e.target.value)}
            onBlur={() => { if (editNotes !== null) saveField("notes", editNotes); setEditNotes(null); }}
            className="w-full px-3 py-2.5 rounded-xl text-white text-sm outline-none placeholder-white/25 resize-none"
            style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.10)" }}
          />
          <p className="text-white/25 text-xs mt-1.5">Blur хийхэд автоматаар хадгалагдана</p>
        </div>

        {/* Delete */}
        <div className="pt-2 border-t" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
          {!confirmDelete ? (
            <button
              onClick={() => setConfirmDelete(true)}
              className="flex items-center gap-2 text-xs text-white/30 hover:text-red-400 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Устгах
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-red-400 text-xs">Устгах уу?</span>
              <button
                onClick={() => deleteMut.mutate({ id })}
                className="px-3 py-1.5 rounded-lg text-xs font-bold text-red-400 transition-all"
                style={{ background: "rgba(248,113,113,0.15)", border: "1px solid rgba(248,113,113,0.30)" }}
              >
                Тийм
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="px-3 py-1.5 rounded-lg text-xs text-white/50 transition-all"
                style={{ background: "rgba(255,255,255,0.06)" }}
              >
                Үгүй
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ManualLeadDialog({
  staffName,
  onClose,
  onCreated,
}: {
  staffName: typeof STAFF_MEMBERS[number];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState("usa");
  const [visaCategory, setVisaCategory] = useState(VISA_CATEGORIES_BY_COUNTRY.usa[0]);
  const createMut = trpc.consultation.createManual.useMutation({
    onSuccess: () => {
      toast.success("Шинэ хүн таны CRM-д нэмэгдлээ.");
      onCreated();
      onClose();
    },
    onError: (error) => toast.error(error.message || "Хүн нэмэхэд алдаа гарлаа."),
  });
  const visaOptions = VISA_CATEGORIES_BY_COUNTRY[country] ?? [];
  const canSubmit = Boolean(name.trim() && phone.trim() && visaCategory);

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/70 p-4 sm:items-center" role="dialog" aria-modal="true" aria-labelledby="manual-lead-title">
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-[#130d3a] p-5 shadow-2xl shadow-black/40 sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-black tracking-[.14em] text-sky-300">{staffName.toUpperCase()} — ӨӨРИЙН CRM</p>
            <h2 id="manual-lead-title" className="mt-1 text-xl font-black text-white">Шинэ хүн нэмэх</h2>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-white/50 transition hover:bg-white/10 hover:text-white" aria-label="Хаах"><X className="h-4 w-4" /></button>
        </div>
        <p className="mt-3 text-sm leading-6 text-white/55">Энд нэмсэн хүн шууд {staffName} ажилтны өөрийн болгосон жагсаалтад орно.</p>
        <div className="mt-5 grid gap-3">
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Үйлчлүүлэгчийн нэр" className="w-full rounded-xl border border-white/10 bg-white/[0.06] px-3 py-3 text-sm text-white outline-none placeholder:text-white/30" />
          <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="Утасны дугаар" className="w-full rounded-xl border border-white/10 bg-white/[0.06] px-3 py-3 text-sm text-white outline-none placeholder:text-white/30" />
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Имэйл хаяг (сонголтоор)" className="w-full rounded-xl border border-white/10 bg-white/[0.06] px-3 py-3 text-sm text-white outline-none placeholder:text-white/30" />
          <select value={country} onChange={e => { const nextCountry = e.target.value; setCountry(nextCountry); setVisaCategory(VISA_CATEGORIES_BY_COUNTRY[nextCountry]?.[0] ?? ""); }} className="w-full rounded-xl border border-white/10 bg-white/[0.06] px-3 py-3 text-sm text-white outline-none">
            {Object.entries(COUNTRY_LABELS).map(([value, label]) => <option key={value} value={value} style={{ background: "#130D3A" }}>{label}</option>)}
          </select>
          <select value={visaCategory} onChange={e => setVisaCategory(e.target.value)} className="w-full rounded-xl border border-white/10 bg-white/[0.06] px-3 py-3 text-sm text-white outline-none">
            {visaOptions.map(option => <option key={option} value={option} style={{ background: "#130D3A" }}>{option}</option>)}
          </select>
        </div>
        <div className="mt-6 flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 rounded-xl border border-white/15 px-4 py-3 text-sm font-bold text-white/65 transition hover:bg-white/10">Болих</button>
          <button type="button" disabled={!canSubmit || createMut.isPending} onClick={() => createMut.mutate({ staffName, name, phone, email, country, visaCategory })} className="flex-1 rounded-xl bg-sky-500 px-4 py-3 text-sm font-black text-white transition hover:bg-sky-400 disabled:cursor-not-allowed disabled:opacity-40">{createMut.isPending ? "Нэмж байна..." : "Хүн нэмэх"}</button>
        </div>
      </div>
    </div>
  );
}

function ClaimDialog({
  lead,
  onClose,
  onClaim,
  pending,
  error,
}: {
  lead: { id: number; name: string };
  onClose: () => void;
  onClaim: (staffName: typeof STAFF_MEMBERS[number]) => void;
  pending: boolean;
  error: string | null;
}) {
  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/70 p-4 sm:items-center" role="dialog" aria-modal="true" aria-labelledby="claim-title">
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-[#130d3a] p-5 shadow-2xl shadow-black/40 sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-black tracking-[.14em] text-sky-300">ХҮСЭЛТ ӨӨРИЙН БОЛГОХ</p>
            <h2 id="claim-title" className="mt-1 text-xl font-black text-white">{lead.name}</h2>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-white/50 transition hover:bg-white/10 hover:text-white" aria-label="Хаах"><X className="h-4 w-4" /></button>
        </div>
        <p className="mt-3 text-sm leading-6 text-white/55">Авах ажилтны нэрийг дармагц хүсэлт шууд тухайн ажилтны цонх руу шилжинэ. Дараа нь өөр ажилтан энэ хүсэлтийг өөрийн болгох боломжгүй болно.</p>
        <div className="mt-5 grid grid-cols-2 gap-2">
          {STAFF_MEMBERS.map(name => (
            <button key={name} type="button" disabled={pending} onClick={() => onClaim(name)} className="rounded-xl border px-3 py-3 text-left text-sm font-bold transition hover:bg-sky-300/15 disabled:cursor-wait disabled:opacity-55" style={{ background: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.72)" }}>
              {pending ? "Хадгалж байна..." : name}
            </button>
          ))}
        </div>
        {error && <p className="mt-4 rounded-xl border border-red-400/30 bg-red-400/10 px-3 py-2.5 text-sm text-red-200">{error}</p>}
        <button type="button" disabled={pending} onClick={onClose} className="mt-6 w-full rounded-xl border border-white/15 px-4 py-3 text-sm font-bold text-white/65 transition hover:bg-white/10 disabled:opacity-50">Болих</button>
      </div>
    </div>
  );
}

/* ── Main Page ───────────────────────────────────────────────── */
export default function StaffCRMPage() {
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("all");
  const [filterCountry, setFilterCountry] = useState("");
  const [search, setSearch] = useState("");
  const [selectedStaff, setSelectedStaff] = useState<typeof STAFF_MEMBERS[number] | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showManualLead, setShowManualLead] = useState(false);
  const [claimLead, setClaimLead] = useState<{ id: number; name: string } | null>(null);
  const [claimError, setClaimError] = useState<string | null>(null);

  const updateMut = trpc.consultation.update.useMutation({ onSuccess: () => refetch() });
  const claimMut = trpc.consultation.claim.useMutation({
    onSuccess: async (_result, variables) => {
      setSelectedStaff(variables.staffName);
      setFilterStatus("all");
      setFilterCountry("");
      setSearch("");
      setSelectedId(null);
      setShowDetail(false);
      setClaimLead(null);
      setClaimError(null);
      await refetch();
      toast.success(`${variables.staffName} ажилтны цонх руу шилжлээ.`);
    },
    onError: (error) => { setClaimError(error.message || "Хүсэлтийг өөрийн болгоход алдаа гарлаа."); refetch(); },
  });

  const { data: allRows = [], refetch } = trpc.consultation.list.useQuery(
    { status: filterStatus, country: filterCountry || undefined, search: search || undefined },
    {
      // New public requests must surface to staff without requiring a manual page refresh.
      refetchInterval: 8_000,
      refetchOnWindowFocus: "always",
      staleTime: 0,
    },
  );

  // Enrich rows with score, sort: hot first, then by score desc
  const rows = useMemo(() => {
    const visibleRows = selectedStaff
      ? allRows.filter(row => row.assignedTo === selectedStaff)
      : allRows.filter(row => isUnclaimedLead(row.assignedTo));
    return [...visibleRows]
      .map(r => ({ ...r, _score: calcLeadScore(r), _urgency: getUrgency(calcLeadScore(r)) }))
      .sort((a, b) => {
        // New requests must be visible immediately, ahead of older in-progress leads.
        const statusDifference = Number(b.status === "new") - Number(a.status === "new");
        if (statusDifference !== 0) return statusDifference;
        const submittedDifference = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        if (submittedDifference !== 0) return submittedDifference;
        return b._score - a._score;
      });
  }, [allRows, selectedStaff]);

  // Keep just-submitted requests visible before the long legacy urgent queue.
  const recentRequestCutoff = Date.now() - 30 * 60 * 1000;
  const recentRequests = rows.filter(row =>
    row.status === "new" && new Date(row.createdAt).getTime() >= recentRequestCutoff,
  );
  const recentRequestIds = new Set(recentRequests.map(row => row.id));
  const hotLeads = rows.filter(row =>
    !recentRequestIds.has(row.id) && row._urgency === "hot" && row.status === "new",
  );
  const otherLeads = rows.filter(row =>
    !recentRequestIds.has(row.id) && !(row._urgency === "hot" && row.status === "new"),
  );

  // Stats
  const stats = useMemo(() => {
    const all = rows.length;
    const urgent = rows.filter(r => r._urgency === "hot").length;
    const waiting = rows.filter(r => r.callTime === "now" && r.status === "new").length;
    const byStatus = { new: 0, phone_consulted: 0, office_consulted: 0, callback: 0 };
    rows.forEach(r => { if (r.status in byStatus) byStatus[r.status as Status]++; });
    return { all, urgent, waiting, ...byStatus };
  }, [rows]);

  const handleQuickNote = (id: number, note: string) => {
    updateMut.mutate({ id, notes: note });
  };

  const handleCardClick = (id: number) => {
    setSelectedId(id);
    setShowDetail(true);
  };

  const handleClaimOpen = (id: number) => {
    const lead = rows.find(row => row.id === id);
    if (!lead || !isUnclaimedLead(lead.assignedTo)) return;
    setClaimError(null);
    setClaimLead({ id: lead.id, name: lead.name });
  };

  return (
    <div className="min-h-screen flex" style={{ background: "#0D0A2E" }}>
      {/* ── Main content ── */}
      <div className="flex flex-col flex-1 min-w-0" style={{ minHeight: "100vh" }}>
        {/* Top bar */}
        <div
          className="flex items-center gap-3 px-5 py-4 flex-wrap sticky top-0 z-10"
          style={{ background: "#0D0A2E", borderBottom: "1px solid rgba(255,255,255,0.08)" }}
        >
          <h1 className="text-white font-bold text-lg mr-auto">CRM — Зөвлөгөө авах хүсэлт</h1>
          {selectedStaff && (
            <button onClick={() => setSelectedStaff(null)} className="rounded-xl border border-sky-300/35 bg-sky-300/10 px-3 py-2 text-xs font-black text-sky-100 transition hover:bg-sky-300/20">
              ← Үндсэн CRM рүү буцах
            </button>
          )}
          <Link href="/staff/performance">
            <a
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all hover:opacity-80"
              style={{ background: "rgba(52,211,153,0.15)", color: "#34d399", border: "1px solid rgba(52,211,153,0.30)" }}
            >
              <BarChart3 className="w-3.5 h-3.5" />
              Гүйцэтгэл
            </a>
          </Link>
          <button
            onClick={() => refetch()}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors text-white/40"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        <div className="border-b border-white/10 bg-white/[0.025] px-4 py-4">
          <div className="mx-auto flex max-w-3xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-black tracking-[.14em] text-sky-300">ХАМТЫН АЖИЛЛАГААНЫ CRM</p>
              <p className="mt-1 text-sm leading-5 text-white/55">Энд зөвхөн эзэнгүй хүсэлтүүд харагдана. “Өөрийн болгох” дарж ажилтны нэрийг сонгоход хүсэлт үндсэн жагсаалтаас шууд алга болж, тухайн ажилтны нэр дээр дарсан хэсэгт шилжинэ.</p>
            </div>
            <span className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-full border border-sky-300/30 bg-sky-300/10 px-3 py-1.5 text-xs font-bold text-sky-200"><Users className="h-3.5 w-3.5" />{STAFF_MEMBERS.length} ажилтан</span>
          </div>
          <div className="mx-auto mt-3 flex max-w-3xl flex-wrap gap-2">
            {STAFF_MEMBERS.map(name => (
              <button
                key={name}
                type="button"
                onClick={() => setSelectedStaff(current => current === name ? null : name)}
                aria-pressed={selectedStaff === name}
                className="rounded-full border px-3 py-1.5 text-xs font-bold transition-all"
                style={{
                  background: selectedStaff === name ? "rgba(96,165,250,0.22)" : "rgba(255,255,255,0.04)",
                  borderColor: selectedStaff === name ? "rgba(125,211,252,0.65)" : "rgba(255,255,255,0.10)",
                  color: selectedStaff === name ? "#e0f2fe" : "rgba(255,255,255,0.70)",
                }}
              >
                {name}
              </button>
            ))}
          </div>
        </div>

        {/* Stats bar */}
        <div
          className="grid grid-cols-4 sm:grid-cols-7 gap-0"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}
        >
          {([
            ["all", "Бүгд", stats.all, "#a78bfa"],
            ["new", "Шинэ", stats.new, "#60a5fa"],
            ["phone_consulted", "Утсаар", stats.phone_consulted, "#34d399"],
            ["callback", "Дахин залгах", stats.callback, "#fbbf24"],
            ["office_consulted", "Оффист", stats.office_consulted, "#c084fc"],
          ] as [FilterStatus, string, number, string][]).map(([s, label, count, color]) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className="flex flex-col items-center py-3 px-2 transition-all"
              style={{
                background: filterStatus === s ? "rgba(124,58,237,0.10)" : "transparent",
                borderBottom: filterStatus === s ? `2px solid ${color}` : "2px solid transparent",
              }}
            >
              <span className="text-xl font-black" style={{ color }}>{count}</span>
              <span className="text-white/35 text-[10px] font-medium mt-0.5 hidden sm:block">{label}</span>
            </button>
          ))}
          {/* Urgent stat */}
          <div className="flex flex-col items-center py-3 px-2">
            <span className="text-xl font-black text-red-400">{stats.urgent}</span>
            <span className="text-white/35 text-[10px] font-medium mt-0.5 hidden sm:block">🔴 Яаралтай</span>
          </div>
          {/* Waiting stat */}
          <div className="flex flex-col items-center py-3 px-2">
            <span className="text-xl font-black" style={{ color: stats.waiting > 0 ? "#f87171" : "rgba(255,255,255,0.30)" }}>
              {stats.waiting}
            </span>
            <span className="text-white/35 text-[10px] font-medium mt-0.5 hidden sm:block">⚡ Хүлээж байна</span>
          </div>
        </div>

        {/* Filters */}
        <div
          className="flex items-center gap-3 px-4 py-3 flex-wrap"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}
        >
          <div className="relative flex-1 min-w-44">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
            <input
              type="text"
              placeholder="Нэр, утас, имэйл..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-lg text-white text-sm outline-none placeholder-white/25"
              style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.10)" }}
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
            <select
              value={filterCountry}
              onChange={e => setFilterCountry(e.target.value)}
              className="pl-9 pr-3 py-2 rounded-lg text-white text-sm outline-none appearance-none cursor-pointer"
              style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.10)", color: filterCountry ? "white" : "rgba(255,255,255,0.40)" }}
            >
              <option value="">Бүх улс</option>
              {Object.entries(COUNTRY_LABELS).map(([v, l]) => (
                <option key={v} value={v} style={{ background: "#130D3A" }}>{l}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Lead list */}
        <div className="p-4 flex flex-col gap-4 max-w-3xl mx-auto w-full">
          {selectedStaff && (
            <div className="flex items-center justify-between gap-3 rounded-xl border border-sky-300/20 bg-sky-300/5 px-3 py-2.5">
              <p className="text-sm font-bold text-sky-100"><span className="text-sky-300">{selectedStaff}</span> — өөрийн болгосон хүмүүс <span className="text-white/45">({rows.length})</span></p>
              <div className="flex shrink-0 items-center gap-3">
                <button type="button" onClick={() => setShowManualLead(true)} className="inline-flex items-center gap-1 text-xs font-black text-sky-100 hover:text-white"><Plus className="h-3.5 w-3.5" />Хүн нэмэх</button>
                <button type="button" onClick={() => setSelectedStaff(null)} className="text-xs font-bold text-sky-200 underline underline-offset-4 hover:text-white">Бүгдийг харах</button>
              </div>
            </div>
          )}
          {rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 gap-3">
              <AlertCircle className="w-10 h-10 text-white/20" />
              <p className="text-white/30 text-sm">{selectedStaff ? `${selectedStaff} ажилтны өөрийн болгосон хүн одоогоор байхгүй байна` : "Хүсэлт олдсонгүй"}</p>
            </div>
          ) : (
            <>
              {recentRequests.length > 0 && (
                <div>
                  <div className="mb-3 flex items-center gap-2">
                    <Zap className="h-4 w-4 text-sky-300" />
                    <h2 className="text-sm font-bold uppercase tracking-wide text-sky-200">
                      Шинэ ирсэн хүсэлт ({recentRequests.length})
                    </h2>
                    <span className="rounded-full border border-sky-300/25 bg-sky-300/10 px-2 py-0.5 text-[10px] font-bold text-sky-100">Сүүлийн 30 минут</span>
                    <div className="h-px flex-1 bg-sky-300/20" />
                  </div>
                  <div className="flex flex-col gap-3">
                    {recentRequests.map(row => (
                      <LeadCard
                        key={row.id}
                        row={row}
                        selected={selectedId === row.id}
                        onClick={() => handleCardClick(row.id)}
                        onQuickNote={handleQuickNote}
                        onClaim={handleClaimOpen}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Hot leads section */}
              {hotLeads.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Flame className="w-4 h-4 text-red-400" />
                    <h2 className="text-red-300 font-bold text-sm uppercase tracking-wide">
                      Яаралтай залгах ({hotLeads.length})
                    </h2>
                    <div className="flex-1 h-px" style={{ background: "rgba(248,113,113,0.25)" }} />
                  </div>
                  <div className="flex flex-col gap-3">
                    {hotLeads.map(row => (
                      <LeadCard
                        key={row.id}
                        row={row}
                        selected={selectedId === row.id}
                        onClick={() => handleCardClick(row.id)}
                        onQuickNote={handleQuickNote}
                        onClaim={handleClaimOpen}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Other leads */}
              {otherLeads.length > 0 && (
                <div>
                  {hotLeads.length > 0 && (
                    <div className="flex items-center gap-2 mb-3">
                      <h2 className="text-white/40 font-semibold text-sm uppercase tracking-wide">
                        Бусад хүсэлт ({otherLeads.length})
                      </h2>
                      <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.08)" }} />
                    </div>
                  )}
                  <div className="flex flex-col gap-3">
                    {otherLeads.map(row => (
                      <LeadCard
                        key={row.id}
                        row={row}
                        selected={selectedId === row.id}
                        onClick={() => handleCardClick(row.id)}
                        onQuickNote={handleQuickNote}
                        onClaim={handleClaimOpen}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Right: Detail Panel (desktop side panel, mobile full-screen modal) ── */}
      {showDetail && selectedId && (
        <>
          {/* Mobile backdrop */}
          <div
            className="fixed inset-0 z-40 lg:hidden"
            style={{ background: "rgba(0,0,0,0.65)" }}
            onClick={() => { setShowDetail(false); setSelectedId(null); }}
          />
          {/* Panel */}
          <div
            className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-sm overflow-y-auto lg:relative lg:z-auto lg:w-96 lg:flex-shrink-0"
            style={{ background: "#0D0A2E", borderLeft: "1px solid rgba(255,255,255,0.08)" }}
          >
            <DetailPanel
              id={selectedId}
              onClose={() => { setShowDetail(false); setSelectedId(null); }}
              onUpdated={() => refetch()}
              staffActionsEnabled={Boolean(selectedStaff)}
            />
          </div>
        </>
      )}
      {showManualLead && selectedStaff && (
        <ManualLeadDialog
          staffName={selectedStaff}
          onClose={() => setShowManualLead(false)}
          onCreated={() => refetch()}
        />
      )}
      {claimLead && (
        <ClaimDialog
          lead={claimLead}
          onClose={() => { if (!claimMut.isPending) { setClaimLead(null); setClaimError(null); } }}
          onClaim={(staffName) => claimMut.mutate({ id: claimLead.id, staffName })}
          pending={claimMut.isPending}
          error={claimError}
        />
      )}
    </div>
  );
}
